"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const events_1 = require("events");
const websocket_1 = require("../const/websocket");
const events_2 = require("../const/events");
// 'runtime' imported by alias
// tslint:disable-next-line
const websocket_2 = __importDefault(require("../runtimes/websocket"));
const parseData = (data) => {
    if (typeof data === "string") {
        try {
            return JSON.parse(data);
        }
        catch (e) {
            return data;
        }
    }
    return data;
};
const defaultOptions = {
    reconnectTimeout: 100,
    maxReconnects: 5,
    clockStopTimeout: 2000,
    maxReconnectWait: 60000,
    requireValidSsl: true,
};
class Connection {
    constructor(path, options, socket) {
        this._attemptConnect = () => {
            if (this._isSocketClosed() && this.state !== events_2.CONNECTION_STATE.CONNECTING) {
                this._setState(events_2.CONNECTION_STATE.CONNECTING, {
                    reconnect: this.reconnecting,
                });
                this.socket.open();
            }
        };
        this._scheduleReconnect = () => {
            if (this.reconnectAttempts >= this.options.maxReconnects ||
                !this.maintainConnection) {
                this._setState(events_2.CONNECTION_STATE.FAILED, {
                    reconnectAttempts: this.reconnectAttempts,
                });
            }
            else {
                this.reconnectAttempts += 1;
                this._setReconnectTimer();
            }
        };
        if (options !== undefined) {
            this.options = Object.assign(defaultOptions, options);
        }
        else {
            this.options = defaultOptions;
        }
        this.uri = path;
        this.eventEmitter = new events_1.EventEmitter();
        this.sendQueue = [];
        this.state = events_2.CONNECTION_STATE.INITIALIZED;
        const wsConfig = {
            url: path,
            useTls: this.options.requireValidSsl,
            requireValidSsl: this.options.requireValidSsl,
        };
        this.socket = socket !== undefined ? socket : websocket_2.default(wsConfig);
        this.socket.on("open", this._handleSocketOpen.bind(this));
        this.socket.on("message", this._handleSocketMessage.bind(this));
        this.socket.on("close", this._handleSocketClose.bind(this));
        this.socket.on("error", this._handleSocketError.bind(this));
        this.socketId = null;
        this.reconnecting = false;
        this.maintainConnection = false;
        this.reconnectTimer = null;
        this.reconnectAttempts = 0;
        this.activityTimeout = null;
        this.activityTimer = null;
        this.pingResponseTimeout = null;
        // clock stop detects when the computer has fallen asleep or the thread has been suspended
        this.clockStopTime = Date.now();
        this.clockStopTimer = (setInterval(this._handleClockStopCheck.bind(this), this.options.clockStopTimeout));
        this.close = this.close.bind(this);
    }
    /* ----------- PUBLIC ----------- */
    open() {
        this.maintainConnection = true;
        this._attemptConnect();
    }
    close() {
        this.maintainConnection = false;
        this._clearReconnectTimer();
        this._clearActivityTimer();
        this.socket.close();
    }
    connected() {
        return this.state === events_2.CONNECTION_STATE.CONNECTED;
    }
    getUri() {
        return this.uri;
    }
    sendEvent(event, data, channel) {
        const payload = {
            event,
            data,
            channel,
        };
        return this._sendWhenReady(payload);
    }
    bind(event, handler) {
        if (handler !== undefined) {
            this.eventEmitter.on(event, handler);
            return;
        }
        return new Promise((resolve) => this.eventEmitter.on(event, resolve));
    }
    /* ----------- PRIVATE ----------- */
    _getWsOptions() {
        const s = {
            rejectUnauthorized: this.options.requireValidSsl !== false,
        };
        return s;
    }
    _handleSocketMessage(event) {
        const eventRaw = JSON.parse(event.data);
        const eventObj = {
            event: eventRaw.event,
            data: eventRaw.data,
            channel: eventRaw.channel,
        };
        const eventName = eventObj.event;
        const data = parseData(eventObj.data);
        switch (eventName) {
            case events_2.SERVER_EVENTS.DOWN.CONNECTION_ESTABLISHED:
                this._handleConnectionEstablished(data);
                break;
            case events_2.SERVER_EVENTS.DOWN.PONG:
                // do nothing, just allow activity timer to be reset
                if (this.pingResponseTimeout !== null) {
                    clearTimeout(this.pingResponseTimeout);
                    this.pingResponseTimeout = null;
                }
                break;
            default:
                this.eventEmitter.emit(events_2.CLIENT_EVENTS.TICK_RECEIVED, eventObj);
                this.eventEmitter.emit(eventName, data);
                break;
        }
        this._setActivityTimer();
        return;
    }
    _handleSocketOpen() {
        this.reconnectAttempts = 0;
        // don't set connected state yet. wait for "connection_established" from server
    }
    _handleSocketClose(data) {
        this._scheduleReconnect();
        this._setState(events_2.CONNECTION_STATE.DISCONNECTED, data);
    }
    _handleSocketError(data) {
        this._scheduleReconnect();
        this._setState(events_2.CONNECTION_STATE.UNAVAILABLE, data);
    }
    _handleConnectionEstablished(data) {
        this.socketId = data.socket_id;
        this.activityTimeout = data.activity_timeout;
        this._setState(events_2.CONNECTION_STATE.CONNECTED, Object.assign({ reconnect: this.reconnecting }, data));
        this._sendAllBuffered();
        this.reconnecting = false;
    }
    _clearReconnectTimer() {
        if (this.reconnectTimer !== null) {
            clearTimeout(this.reconnectTimer);
        }
    }
    _setReconnectTimer() {
        this._clearReconnectTimer();
        const timeout = this.options.reconnectTimeout * Math.pow(2, this.reconnectAttempts) + 1;
        const clamped = timeout < this.options.maxReconnectWait
            ? timeout
            : this.options.maxReconnectWait;
        this.reconnectTimer = setTimeout(this._handleReconnectTick.bind(this), clamped);
    }
    _handleReconnectTick() {
        if (this._isSocketClosed() &&
            this.state !== events_2.CONNECTION_STATE.CONNECTED &&
            this.state !== events_2.CONNECTION_STATE.CONNECTING) {
            this.reconnecting = true;
            this._setState(events_2.CONNECTION_STATE.RECONNECTING, {
                reconnect: this.reconnecting,
            });
            this._attemptConnect();
        }
    }
    _clearActivityTimer() {
        if (this.activityTimer !== null) {
            clearTimeout(this.activityTimer);
        }
    }
    _setActivityTimer() {
        this._clearActivityTimer();
        this.activityTimer = setInterval(this._handleActivityTick.bind(this), (this.activityTimeout !== null ? this.activityTimeout : 60) * 1000);
    }
    _handleActivityTick() {
        this.sendEvent(events_2.SERVER_EVENTS.UP.PING, {});
        this.pingResponseTimeout = setTimeout(this._handlePingResponseTimeout.bind(this), 1000);
    }
    _handlePingResponseTimeout() {
        // the timer should be deleted if ping has come back
        this.socket.close();
        this._handleSocketError({ reason: "no_ping_reply" });
    }
    _sendAllBuffered() {
        this.sendQueue.forEach(({ payload, resolve }) => {
            this.socket.send(JSON.stringify(payload));
            resolve();
        });
        this.sendQueue = [];
    }
    _sendWhenReady(payload) {
        if (this.state === events_2.CONNECTION_STATE.CONNECTED) {
            this.socket.send(JSON.stringify(payload));
            this._setActivityTimer();
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            this.sendQueue.push({
                payload,
                resolve,
            });
        });
    }
    _setState(state, data) {
        this.state = state;
        this.eventEmitter.emit(state, data);
    }
    _isSocketClosed() {
        return (this.socket.state() === websocket_1.WEBSOCKET_READYSTATE.CLOSED ||
            this.socket.state() === websocket_1.WEBSOCKET_READYSTATE.CLOSING);
    }
    _handleClockStopCheck() {
        const now = Date.now();
        if (now - this.clockStopTime > this.options.clockStopTimeout * 2) {
            // there is some kind of clock drift, likely the computer was asleep
            // check if the connection is still alive with a ping/pong
            this._handleActivityTick();
        }
        this.clockStopTime = now;
    }
}
exports.Connection = Connection;
